<template>
    <Layout>
        <div class="space-y-4 p-4 pt-16 lg:space-y-8">
            <div class="mx-auto max-w-4xl">
                <form class="flex flex-col overflow-hidden rounded-xl border border-black md:hidden" @submit.prevent="handleSearch">
                    <div class="border-b border-black">
                        <div class="relative w-full">
                            <input
                                type="text"
                                name="query"
                                id="query"
                                v-model="query"
                                :placeholder="$t('Event title, company, keyword')"
                                class="h-12 w-full border-none pl-10 focus:border-none focus:outline-none focus:ring-0"
                            />
                            <IconSearch class="pointer-events-none absolute left-3 top-3.5 h-5 w-5 select-none text-gray-500" />
                        </div>
                    </div>

                    <div>
                        <div class="relative w-full">
                            <CityAutocomplete
                                v-model="city"
                                @latlng="setLatLng"
                                class="h-12 pl-10"
                                :placeholder="$t('Search by city')"
                                borderless
                            />
                            <IconMapPin class="pointer-events-none absolute left-3 top-3.5 h-5 w-5 select-none text-gray-500" />
                        </div>
                    </div>

                    <button class="button h-12 rounded-t-none">{{ $t('Search') }}</button>
                </form>

                <!-- Desktop version -->
                <form
                    class="hidden items-center rounded-xl border border-black bg-white p-2 shadow-xl md:flex"
                    @submit.prevent="handleSearch"
                >
                    <div class="flex-1">
                        <div class="relative w-full">
                            <input
                                type="text"
                                name="query"
                                id="query"
                                v-model="query"
                                :placeholder="$t('Event title, company, keyword')"
                                class="h-12 w-full border-none pl-10 focus:border-none focus:outline-none focus:ring-0"
                            />
                            <IconSearch class="pointer-events-none absolute left-3 top-3.5 h-5 w-5 select-none text-gray-500" />
                        </div>
                    </div>

                    <div class="h-8 w-px bg-gray-200"></div>

                    <div class="flex flex-1 pl-8">
                        <div class="relative w-full">
                            <CityAutocomplete
                                v-model="city"
                                @latlng="setLatLng"
                                class="h-12 pl-10"
                                :placeholder="$t('Search by city')"
                                borderless
                            />
                            <IconMapPin class="pointer-events-none absolute left-3 top-3.5 h-5 w-5 select-none text-gray-500" />
                        </div>

                        <button class="button h-12">{{ $t('Search') }}</button>
                    </div>
                </form>
            </div>

            <div v-if="params.recherche || params.ville" class="flex items-center justify-center gap-4">
                <div v-if="params.recherche" class="flex items-center gap-2 rounded bg-white px-4 py-2 text-xs shadow">
                    <p class="text-gray-600">{{ $t('Keywords') }}</p>
                    <p>:</p>
                    <p class="font-medium">{{ params.recherche }}</p>
                    <button @click="clearQuery">&times;</button>
                </div>

                <div v-if="params.ville" class="flex items-center gap-2 rounded bg-white px-4 py-2 text-xs shadow">
                    <p class="text-gray-600">{{ $t('City') }}</p>
                    <p>:</p>
                    <p class="font-medium">{{ params.ville }}</p>
                    <button @click="clearCity">&times;</button>
                </div>
            </div>

            <div class="mx-auto max-w-[1200px]">
                <div class="grid gap-4 lg:grid-cols-2 lg:gap-8 xl:grid-cols-3">
                    <EventCard v-for="event in events" :key="event.title" :event="event" />

                    <div v-if="!events.length" class="col-span-full">
                        <div
                            class="mx-auto flex max-w-xl flex-col items-center justify-center gap-6 rounded-xl bg-white p-16 text-center shadow"
                        >
                            <p class="text-lg font-medium">{{ $t('No events found') }}</p>
                            <p class="text-gray-600">{{ $t('No events found matching your search criterias.') }}</p>
                            <InertiaLink class="button" :href="$page.props.locale == 'fr' ? '/evenements' : '/en/events'">
                                {{ $t('View all the events') }}
                            </InertiaLink>
                        </div>
                    </div>

                    <div ref="loadMoreRef"></div>
                </div>
            </div>
        </div>
    </Layout>
</template>

<script setup>
    import { onMounted, ref } from 'vue';
    import { useIntersectionObserver, useUrlSearchParams } from '@vueuse/core';
    import EventCard from '@/Partials/EventCard.vue';
    import CityAutocomplete from '@/Shared/CityAutocomplete.vue';
    import { useTypesenseEvents } from '@/useTypesenseEvents';
    import { IconMapPin, IconSearch } from '@tabler/icons-vue';

    const props = defineProps({
        filters: Object,
    });

    let query = ref(props.filters.query || '');
    let city = ref(props.filters.city || '');
    let lat = ref(props.filters.lat || '');
    let lng = ref(props.filters.lng || '');

    // Sync URL params with query and city
    const params = useUrlSearchParams('history');

    // Typesense
    const { events, search, loadMore } = useTypesenseEvents();

    // Infinite scroll
    const loadMoreRef = ref();
    useIntersectionObserver(loadMoreRef, ([entry], observerElement) => {
        if (entry?.isIntersecting || false) {
            loadMore();
        }
    });

    onMounted(() => {
        handleSearch();
    });

    function handleSearch() {
        params.recherche = query.value;
        params.ville = city.value;

        search(query.value, lat.value, lng.value, 1, props.company?.slug);
    }

    function clearQuery() {
        query.value = '';
        params.recherche = '';

        handleSearch();
    }

    function clearCity() {
        city.value = '';
        lat.value = '';
        lng.value = '';

        params.ville = '';
        params.lat = '';
        params.lng = '';

        handleSearch();
    }

    function setLatLng(latlng) {
        city.value = latlng.city;
        lat.value = latlng.lat;
        lng.value = latlng.lng;

        params.ville = latlng.city;
        params.lat = latlng.lat;
        params.lng = latlng.lng;

        handleSearch();
    }
</script>
